<template>
  <div class="depense-schedules-panel">
    <div
      :class="[
        'd-flex align-items-center mb-3',
        showSectionTitle ? 'justify-content-between' : 'justify-content-end',
      ]"
    >
      <h5 v-if="showSectionTitle" class="mb-0">{{ $t("fournisseur.schedulingStatus") }}</h5>
    </div>

    <a-alert
      v-if="invoiceIsPaid"
      type="info"
      show-icon
      class="mb-3"
      :message="$t('fournisseur.schedulesDisabledInvoicePaid')"
    />

    <!-- Paid installments (read-only) -->
    <template v-if="paidSchedules.length">
      <div class="text-muted small font-weight-bold mb-2">{{ $t("fournisseur.paid") }}</div>
      <a-table
        row-key="_id"
        :columns="paidColumns"
        :data-source="paidSchedules"
        :pagination="false"
        size="small"
        class="mb-4"
      >
        <template slot="dueDate" slot-scope="text">
          {{ formatDate(text) }}
        </template>
        <template slot="amount" slot-scope="text">
          {{ text != null ? text.toFixed(3) : "—" }}
        </template>
      </a-table>
    </template>

    <!-- Pending: batch edit then single Save -->
    <div v-if="!invoiceIsPaid" class="pending-draft-section">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
        <span class="font-weight-bold">{{ $t("saisiePaiement.pendingSchedules") }}</span>
        <a-button type="dashed" size="small" icon="plus" :disabled="!canAddSchedules" @click="addDraftRow">
          {{ $t("fournisseur.addSchedule") }}
        </a-button>
      </div>

      <div v-if="draftRows.length === 0 && !loadingSchedules" class="text-muted small mb-3">
        {{ $t("saisiePaiement.batchSchedulesEmptyHint") }}
      </div>

      <div
        v-for="row in draftRows"
        :key="row.key"
        :class="['draft-row mb-2 d-flex flex-wrap align-items-center', { 'draft-row-highlight': isHighlightDraftRow(row) }]"
      >
        <a-date-picker
          v-model="row.dueDate"
          format="DD/MM/YYYY"
          class="draft-row-date"
          :placeholder="$t('fournisseur.dueDate')"
          @change="touchDraft"
        />
        <a-input-number
          v-model="row.amount"
          :min="0.001"
          :step="0.001"
          class="draft-row-amount ml-2"
          :placeholder="$t('fournisseur.scheduledAmount')"
          @change="touchDraft"
        />
        <a-button type="link" class="text-danger ml-1" @click="removeDraftRow(row.key)">
          <a-icon type="delete" />
        </a-button>
      </div>

      <div
        class="sum-match-bar mt-3 mb-3"
        :class="{ 'sum-match-bar--ok': sumMatches, 'sum-match-bar--bad': !sumMatches && draftRows.length > 0 }"
      >
        <span>
          {{ $t("saisiePaiement.batchPendingTotal") }}: <strong>{{ draftSum.toFixed(3) }}</strong>
          — {{ $t("saisiePaiement.batchRequiredRemaining") }}: <strong>{{ remainingToSchedule.toFixed(3) }}</strong>
        </span>
        <a-icon v-if="sumMatches && draftRows.length" type="check-circle" class="ml-2 text-success" />
        <a-icon v-else-if="draftRows.length && !sumMatches" type="exclamation-circle" class="ml-2 text-warning" />
      </div>

      <div class="mb-4 schedule-batch-actions">
        <a-button type="primary" :loading="savingBatch" :disabled="!canSaveBatch" @click="saveBatch">
          {{ $t("action.enregistrer") }}
        </a-button>
        <a-button :disabled="!draftDirty" @click="resetDraft">{{ $t("action.annuler") }}</a-button>
        <span v-if="draftDirty" class="text-muted small">{{ $t("saisiePaiement.unsavedSchedulesHint") }}</span>
      </div>
    </div>

    <!-- After save: pending lines read-only when payment flow is hidden (e.g. dette drawer) -->
    <template
      v-if="hidePaymentFlow && !invoiceIsPaid && !draftDirty && pendingSchedulesFromServer.length"
    >
      <div class="text-muted small font-weight-bold mb-2">{{ $t("saisiePaiement.pendingSchedules") }}</div>
      <a-table
        row-key="_id"
        :columns="pendingPayColumns"
        :data-source="pendingSchedulesFromServer"
        :pagination="false"
        size="small"
        :loading="loadingSchedules"
        class="mb-3"
      >
        <template slot="dueDate" slot-scope="text">
          {{ formatDate(text) }}
        </template>
        <template slot="amount" slot-scope="text">
          {{ text != null ? text.toFixed(3) : "—" }}
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="scheduleStatusColor(text)">{{ scheduleStatusLabel(text) }}</a-tag>
        </template>
      </a-table>
    </template>

    <!-- After save: select pending lines to pay -->
    <template
      v-if="!hidePaymentFlow && !invoiceIsPaid && !draftDirty && pendingSchedulesFromServer.length"
    >
      <div class="text-muted small font-weight-bold mb-2">{{ $t("saisiePaiement.selectToPaySection") }}</div>
      <a-table
        row-key="_id"
        :columns="pendingPayColumns"
        :data-source="pendingSchedulesFromServer"
        :pagination="false"
        size="small"
        :row-selection="rowSelection"
        :loading="loadingSchedules"
        class="mb-3"
      >
        <template slot="dueDate" slot-scope="text">
          {{ formatDate(text) }}
        </template>
        <template slot="amount" slot-scope="text">
          {{ text != null ? text.toFixed(3) : "—" }}
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="scheduleStatusColor(text)">{{ scheduleStatusLabel(text) }}</a-tag>
        </template>
      </a-table>
    </template>

    <div v-if="schedules.length === 0 && !loadingSchedules" class="text-center text-muted mt-3">
      {{ $t("fournisseur.noSchedules") }}
    </div>

    <div
      v-else-if="!hidePaymentFlow && !invoiceIsPaid && !draftDirty && pendingSchedulesFromServer.length"
      class="pay-bar mt-3"
    >
      <span
        >{{ $t("saisiePaiement.totalSelected") }}: <span class="amount-cell">{{ selectedTotal.toFixed(3) }}</span></span
      >
      <a-button type="primary" class="action-button" :disabled="!selectedIds.length" @click="openPayModal">
        {{ $t("saisiePaiement.paySelected") }} ({{ selectedIds.length }})
      </a-button>
    </div>

    <a-modal
      v-if="!hidePaymentFlow"
      wrap-class-name="depense-schedule-payment-modal"
      :visible="payModalVisible"
      :title="$t('saisiePaiement.confirmPayment')"
      :confirm-loading="paying"
      :width="'70%'"
      @ok="submitPayment"
      @cancel="payModalVisible = false"
      destroy-on-close
    >
      <div class="mb-3">
        <div class="text-muted mb-1">{{ $t("saisiePaiement.totalSelected") }}</div>
        <a-input-number :value="selectedTotal" disabled style="width: 100%" :precision="3" />
      </div>
      <immediate-payment-form
        ref="paymentForm"
        :payment-sources="paymentSources"
        :total-amount="selectedTotal"
        :hide-submit="true"
      />
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import { fetchSchedules, replacePendingSchedules } from "@/services/api/compV3/suppliers";
import { executeGroupedPayment } from "@/services/api/compV3/paymentEntry";
import { fetchDecaissementLookups } from "@/services/api/compV3/decaissement";
import { showApiError } from "@/utilities/showApiError";
import ImmediatePaymentForm from "../../components/ImmediatePaymentForm.vue";

function newDraftKey() {
  return `d-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default {
  name: "DepenseSchedulesPanel",
  components: { ImmediatePaymentForm },
  props: {
    depense: { type: Object, default: null },
    highlightScheduleId: { type: [String, Object], default: null },
    showSectionTitle: { type: Boolean, default: true },
    /** When true, hide pay-from-schedule (saisie paiement); only batch-edit pending schedules. */
    hidePaymentFlow: { type: Boolean, default: false },
  },
  data() {
    return {
      schedules: [],
      loadingSchedules: false,
      draftRows: [],
      draftBaseline: "",
      savingBatch: false,
      selectedIds: [],
      payModalVisible: false,
      paying: false,
      paymentSources: { myCaisses: [], otherCaisses: [], depenseCashCaisses: [], caisseBanks: [] },
    };
  },
  computed: {
    depenseTotal() {
      return Number(this.depense?._totalAmount || 0);
    },
    paidSchedules() {
      return (this.schedules || []).filter((s) => s.status === "paid").sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    },
    paidSum() {
      return this.paidSchedules.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    },
    /** Amount pending lines must sum to (invoice total − paid). */
    remainingToSchedule() {
      const r = this.depenseTotal - this.paidSum;
      return Number(r.toFixed(3));
    },
    pendingSchedulesFromServer() {
      return (this.schedules || []).filter((s) => s.status === "pending");
    },
    draftSum() {
      return this.draftRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    },
    sumMatches() {
      if (this.remainingToSchedule <= 0.0005) {
        return this.draftRows.length === 0 || Math.abs(this.draftSum) < 0.0005;
      }
      return Math.abs(this.draftSum - this.remainingToSchedule) < 0.009;
    },
    draftDirty() {
      return this.serializeDraft() !== this.draftBaseline;
    },
    canSaveBatch() {
      if (!this.depense || this.invoiceIsPaid || !this.draftDirty) return false;
      if (!this.sumMatches) return false;
      if (this.remainingToSchedule > 0.0005 && this.draftRows.length === 0) return false;
      for (const r of this.draftRows) {
        if (!r.dueDate || r.amount == null || Number(r.amount) <= 0) return false;
      }
      return true;
    },
    paidColumns() {
      return [
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dueDate", key: "due", scopedSlots: { customRender: "dueDate" } },
        { title: this.$t("fournisseur.scheduledAmount"), dataIndex: "amount", key: "amt", scopedSlots: { customRender: "amount" } },
      ];
    },
    pendingPayColumns() {
      return [
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dueDate", key: "due", scopedSlots: { customRender: "dueDate" } },
        { title: this.$t("fournisseur.scheduledAmount"), dataIndex: "amount", key: "amt", scopedSlots: { customRender: "amount" } },
        { title: this.$t("action.status"), dataIndex: "status", key: "st", scopedSlots: { customRender: "status" } },
      ];
    },
    pendingSchedules() {
      return this.pendingSchedulesFromServer;
    },
    selectedRows() {
      const idSet = new Set(this.selectedIds);
      return this.pendingSchedules.filter((s) => idSet.has(String(s._id)));
    },
    selectedTotal() {
      return this.selectedRows.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    },
    invoiceIsPaid() {
      return !!(this.depense && this.depense.invoiceStatus === "paid");
    },
    canAddSchedules() {
      return !!(this.depense && this.depense.invoiceStatus !== "paid");
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedIds,
        onChange: this.onSelectionChange,
        getCheckboxProps: (record) => ({ props: { disabled: record.status !== "pending" } }),
      };
    },
  },
  watch: {
    depense: {
      immediate: true,
      handler(d) {
        if (d && d._id) {
          this.loadSchedules();
          if (!this.hidePaymentFlow) this.loadPaymentSources();
        } else {
          this.schedules = [];
          this.draftRows = [];
          this.draftBaseline = "";
          this.selectedIds = [];
        }
      },
    },
    draftDirty(dirty) {
      if (dirty) this.selectedIds = [];
    },
  },
  methods: {
    isHighlightDraftRow(row) {
      if (!this.highlightScheduleId || !row.scheduleId) return false;
      return String(row.scheduleId) === String(this.highlightScheduleId);
    },
    serializeDraft() {
      return JSON.stringify(
        this.draftRows.map((r) => ({
          due: r.dueDate && r.dueDate.toISOString ? r.dueDate.toISOString() : r.dueDate,
          amt: r.amount,
        }))
      );
    },
    touchDraft() {
      this.$forceUpdate();
    },
    scheduleStatusColor(status) {
      if (status === "paid") return "green";
      if (status === "cancelled") return "default";
      return "blue";
    },
    scheduleStatusLabel(status) {
      if (status === "paid") return this.$t("fournisseur.paid");
      if (status === "cancelled") return this.$t("action.annuler");
      return this.$t("saisiePaiement.pending");
    },
    formatDate(val) {
      return val ? moment(val).format("DD/MM/YYYY") : "—";
    },
    buildDraftFromSchedules() {
      const pending = (this.schedules || []).filter((s) => s.status === "pending");
      return pending.map((s) => ({
        key: `s-${s._id}`,
        scheduleId: s._id,
        dueDate: moment(s.dueDate),
        amount: Number(s.amount),
      }));
    },
    setBaselineFromDraft() {
      this.draftBaseline = this.serializeDraft();
    },
    async loadSchedules() {
      if (!this.depense || !this.depense._id) return;
      this.loadingSchedules = true;
      try {
        const res = await fetchSchedules(this.depense._id);
        this.schedules = res.data || [];
        this.draftRows = this.buildDraftFromSchedules();
        this.setBaselineFromDraft();
        const validIds = new Set(this.pendingSchedulesFromServer.map((s) => String(s._id)));
        this.selectedIds = this.selectedIds.filter((id) => validIds.has(String(id)));
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.loadingSchedules = false;
      }
    },
    async loadPaymentSources() {
      try {
        const res = await fetchDecaissementLookups();
        const ps = res.data?.paymentSources || {};
        this.paymentSources = {
          myCaisses: ps.myCaisses || (ps.myCaisse ? [ps.myCaisse] : []),
          otherCaisses: ps.otherCaisses || [],
          depenseCashCaisses: ps.depenseCashCaisses || [],
          caisseBanks: ps.caisseBanks || [],
        };
      } catch {
        this.paymentSources = { myCaisses: [], otherCaisses: [], depenseCashCaisses: [], caisseBanks: [] };
      }
    },
    addDraftRow() {
      if (!this.canAddSchedules) {
        this.$message.warning(this.$t("fournisseur.schedulesDisabledInvoicePaid"));
        return;
      }
      this.draftRows.push({
        key: newDraftKey(),
        scheduleId: null,
        dueDate: null,
        amount: null,
      });
    },
    removeDraftRow(key) {
      this.draftRows = this.draftRows.filter((r) => r.key !== key);
    },
    resetDraft() {
      this.draftRows = this.buildDraftFromSchedules();
      this.setBaselineFromDraft();
    },
    async saveBatch() {
      if (!this.canSaveBatch) return;
      this.savingBatch = true;
      try {
        const schedules = this.draftRows.map((r) => {
          const due = r.dueDate && r.dueDate.toDate ? r.dueDate.toDate() : r.dueDate;
          return { dueDate: due, amount: Number(r.amount) };
        });
        await replacePendingSchedules(this.depense._id, schedules);
        this.$message.success(this.$t("saisiePaiement.batchSchedulesSaved"));
        await this.loadSchedules();
        this.$emit("refresh");
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.savingBatch = false;
      }
    },
    onSelectionChange(keys) {
      this.selectedIds = keys.map((k) => String(k));
    },
    openPayModal() {
      if (!this.selectedIds.length) {
        this.$message.warning(this.$t("saisiePaiement.selectAtLeastOne"));
        return;
      }
      this.payModalVisible = true;
      this.$nextTick(() => {
        if (this.$refs.paymentForm && this.$refs.paymentForm.reset) this.$refs.paymentForm.reset();
      });
    },
    async submitPayment() {
      if (!this.$refs.paymentForm) return;
      const payload = this.$refs.paymentForm.buildPayload();
      if (!payload) return;
      this.paying = true;
      try {
        await executeGroupedPayment({
          scheduleIds: this.selectedIds,
          splits: payload.splits,
          receiptNumber: payload.receiptNumber || "",
        });
        this.$message.success(this.$t("saisiePaiement.paymentExecuted"));
        this.payModalVisible = false;
        this.selectedIds = [];
        await this.loadSchedules();
        this.$emit("refresh");
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.paying = false;
      }
    },
  },
};
</script>

<style scoped>
.schedule-batch-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.draft-row {
  gap: 8px;
}
.draft-row-date {
  min-width: 150px;
}
.draft-row-amount {
  width: 140px;
  max-width: 100%;
}
.draft-row-highlight {
  border-left: 3px solid #1890ff;
  padding-left: 10px;
  margin-left: -4px;
}
.sum-match-bar {
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.sum-match-bar--ok {
  border-color: #b7eb8f;
  background: #f6ffed;
}
.sum-match-bar--bad {
  border-color: #ffe58f;
  background: #fffbe6;
}
.pay-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 8px;
  padding: 12px 16px;
}
</style>

<!-- Modal is teleported; footer buttons need non-scoped rules -->
<style>
.depense-schedule-payment-modal .ant-modal-footer .ant-btn + .ant-btn {
  margin-left: 10px;
}
</style>
